// src/pages/offlineSales/components/CustomerModal.jsx
import React, { useState, useEffect } from 'react';
import { X, User, Mail, MapPin, FileText, Hash, Tag, Layers, Plus, Check, DollarSign, Lock } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import {
  createCustomer, updateCustomer, getCustomerTypes, getCustomers, getCustomerCategories, createCustomerCategory,
} from '../../../services/api/offlineSales/customers';
import { getCities } from '../../../services/api/orderReference';
import useCitySearch from '../../../hooks/useCitySearch';
import PhoneNumberInput from '../../../components/ui/PhoneNumberInput/PhoneNumberInput';
import Dropdown from '../../../components/ui/Dropdown/Dropdown';
import DatePicker from '../../../components/ui/DatePicker/DatePicker';
import CustomerLocationPicker from './CustomerLocationPicker';
import {
  isBarePhone, phoneKey, CUS_CODE_RE, todayStr, validateCustomerForm, BACKEND_FIELD_MAP,
} from '../../../utils/customerValidation';
import sharedStyles from './OfflineSalesSharedModals.module.css';
import styles from './CustomerModal.module.css';

// Map known backend validation messages back onto their field
const BACKEND_FIELD_ERRORS = [
  ['phone number already exists', 'phone', 'offlineSales.phoneExists'],
  ['Customer code already exists', 'cusCode', 'offlineSales.cusCodeExists'],
  ['Secondary phone', 'secondaryPhone', 'offlineSales.secondarySameAsPrimary'],
  ['As of date', 'obDate', 'offlineSales.obDateFuture'],
];

const CustomerModal = ({ customer, onClose, onSaved }) => {
  const { t, formatCurrency } = useLocalization();
  const { success, error } = useAlert();
  const isEdit = Boolean(customer?.id);
  const obPaid = parseFloat(customer?.openingBalance?.paidAmount || 0);
  const obLocked = obPaid > 0;

  const [form, setForm] = useState({
    cusCode: '',
    name: '',
    phone: '',
    secondaryPhone: '',
    email: '',
    address: '',
    notes: '',
    cityId: null,
    isActive: true,
    latitude: null,
    longitude: null,
    creditLimit: '',
    creditTermDays: '',
    customerTypeId: null,
    customerCategoryId: null,
    tinNumber: '',
    vatNumber: '',
  });
  const [openingBalance, setOpeningBalance] = useState({ amount: '', asOfDate: '' });
  const [saving, setSaving] = useState(false);
  const [cities, setCities] = useState([]);
  const [customerTypes, setCustomerTypes] = useState([]);
  const [customerCategories, setCustomerCategories] = useState([]);
  const [showCategoryCreate, setShowCategoryCreate] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [phoneFormatErrors, setPhoneFormatErrors] = useState({});
  const [remoteErrors, setRemoteErrors] = useState({});

  const {
    searchTerm: citySearch,
    filteredCities,
    selectedIndex: citySelectedIndex,
    showResults: showCityResults,
    selectedCity,
    handleSearchChange: handleCitySearchChange,
    handleKeyDown: handleCityKeyDown,
    selectCity,
    clearSearch: clearCitySearch,
    hideResults: hideCityResults,
    showResultsDropdown: showCityDropdown,
  } = useCitySearch(cities);

  useEffect(() => {
    getCities().then(data => setCities(Array.isArray(data) ? data : (data?.cities || []))).catch(() => {});
    getCustomerTypes().then(data => setCustomerTypes(Array.isArray(data) ? data : [])).catch(() => {});
    getCustomerCategories().then(data => setCustomerCategories(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (customer) {
      setForm({
        cusCode: customer.cusCode || '',
        name: customer.name || '',
        phone: customer.phone || '',
        secondaryPhone: customer.secondaryPhone || '',
        email: customer.email || '',
        address: customer.address || '',
        notes: customer.notes || '',
        cityId: customer.cityId || null,
        isActive: customer.isActive !== false,
        latitude: customer.latitude != null ? Number(customer.latitude) : null,
        longitude: customer.longitude != null ? Number(customer.longitude) : null,
        creditLimit: customer.creditLimit != null ? String(customer.creditLimit) : '',
        creditTermDays: customer.creditTermDays != null ? String(customer.creditTermDays) : '',
        customerTypeId: customer.customerTypeId || null,
        customerCategoryId: customer.customerCategoryId || null,
        tinNumber: customer.tinNumber || '',
        vatNumber: customer.vatNumber || '',
      });
      setOpeningBalance({
        amount: customer.openingBalance ? String(customer.openingBalance.amount) : '',
        asOfDate: customer.openingBalance?.asOfDate ? String(customer.openingBalance.asOfDate).slice(0, 10) : '',
      });
      if (customer.city) {
        selectCity(customer.city);
      }
    }
  }, [customer]); // eslint-disable-line react-hooks/exhaustive-deps

  // Editing a field clears the server-side error shown for it
  const clearRemote = (field) => setRemoteErrors(r => (r[field] ? { ...r, [field]: '' } : r));
  const set = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
    clearRemote(field);
  };
  const touch = (field) => () => setTouched(tc => ({ ...tc, [field]: true }));

  // Uniqueness checks (advisory — the backend enforces them on save)
  useEffect(() => {
    if (isBarePhone(form.phone)) { setRemoteErrors(r => ({ ...r, phone: '' })); return undefined; }
    const key = phoneKey(form.phone);
    const timer = setTimeout(async () => {
      try {
        const result = await getCustomers({ search: key, limit: 5, includeInactive: true });
        const taken = (result.customers || []).some(c => c.id !== customer?.id && phoneKey(c.phone) === key);
        setRemoteErrors(r => ({ ...r, phone: taken ? t('offlineSales.phoneExists') : '' }));
      } catch { /* the backend check on save still applies */ }
    }, 400);
    return () => clearTimeout(timer);
  }, [form.phone]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const code = form.cusCode.trim().toUpperCase();
    if (!code || !CUS_CODE_RE.test(code)) { setRemoteErrors(r => ({ ...r, cusCode: '' })); return undefined; }
    const timer = setTimeout(async () => {
      try {
        const result = await getCustomers({ search: code, limit: 5, includeInactive: true });
        const taken = (result.customers || []).some(c => c.id !== customer?.id && (c.cusCode || '').toUpperCase() === code);
        setRemoteErrors(r => ({ ...r, cusCode: taken ? t('offlineSales.cusCodeExists') : '' }));
      } catch { /* the backend check on save still applies */ }
    }, 400);
    return () => clearTimeout(timer);
  }, [form.cusCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const localErrors = validateCustomerForm({ form, openingBalance, obLocked }, t);
  const phoneErrors = {
    phone: isBarePhone(form.phone) ? '' : phoneFormatErrors.phone,
    secondaryPhone: isBarePhone(form.secondaryPhone) ? '' : phoneFormatErrors.secondaryPhone,
  };
  const allErrors = { ...localErrors };
  Object.entries({ ...phoneErrors, ...remoteErrors }).forEach(([k, v]) => { if (v && !allErrors[k]) allErrors[k] = v; });
  const hasErrors = Object.values(allErrors).some(Boolean);
  const fieldError = (field) => ((touched[field] || submitAttempted || remoteErrors[field]) ? allErrors[field] : '');
  const errorText = (field) => (fieldError(field) ? <span className={styles.errorText}>{fieldError(field)}</span> : null);
  // PhoneNumberInput renders its own format error; only pass errors it doesn't know about
  const extraPhoneError = (field) => (fieldError(field) && fieldError(field) !== phoneErrors[field] ? fieldError(field) : undefined);

  const handleCitySelect = (city) => {
    selectCity(city);
    setForm(f => ({ ...f, cityId: city.id }));
  };

  const handleCityKeyDownWrapper = (e) => {
    const selected = handleCityKeyDown(e);
    if (selected) handleCitySelect(selected);
  };

  const handleClearCity = () => {
    clearCitySearch();
    setForm(f => ({ ...f, cityId: null }));
  };

  const selectedLocation = form.latitude != null && form.longitude != null
    ? { latitude: form.latitude, longitude: form.longitude }
    : null;

  const handleLocationChange = (loc) => {
    setForm(f => ({ ...f, latitude: loc?.latitude ?? null, longitude: loc?.longitude ?? null }));
  };

  const handleCancelCategoryCreate = () => {
    setShowCategoryCreate(false);
    setNewCategoryName('');
  };

  const handleCreateCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    try {
      setCreatingCategory(true);
      const { category } = await createCustomerCategory({ name });
      setCustomerCategories(list => [...list.filter(c => c.id !== category.id), category]
        .sort((a, b) => a.name.localeCompare(b.name)));
      setForm(f => ({ ...f, customerCategoryId: category.id }));
      success(t('offlineSales.customerCategoryCreated', { name: category.name }));
      handleCancelCategoryCreate();
    } catch (err) {
      error(err?.response?.data?.message || t('offlineSales.customerCategoryCreateFailed'));
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (hasErrors) return;
    const obAmount = openingBalance.amount === '' ? null : parseFloat(openingBalance.amount);
    try {
      setSaving(true);
      const payload = {
        ...form,
        cusCode: form.cusCode.trim().toUpperCase() || null,
        phone: isBarePhone(form.phone) ? '' : form.phone,
        secondaryPhone: isBarePhone(form.secondaryPhone) ? '' : form.secondaryPhone,
        creditLimit: form.creditLimit !== '' ? parseFloat(form.creditLimit) : null,
        creditTermDays: form.creditTermDays !== '' ? parseInt(form.creditTermDays) : null,
        ...(obLocked ? {} : { openingBalance: { amount: obAmount, asOfDate: openingBalance.asOfDate || null } }),
      };
      const result = isEdit
        ? await updateCustomer(customer.id, payload)
        : await createCustomer(payload);
      onSaved(result.customer);
    } catch (err) {
      const data = err?.response?.data || {};
      const message = data.message || 'Failed to save customer';
      if (Array.isArray(data.errors) && data.errors.length) {
        // Joi validation failures — every field error at once
        setRemoteErrors(r => data.errors.reduce((acc, { field, message: msg }) => {
          const key = BACKEND_FIELD_MAP[field] || field || '';
          return key ? { ...acc, [key]: acc[key] ? `${acc[key]}; ${msg}` : msg } : acc;
        }, { ...r }));
        const unmapped = data.errors.filter(e => !e.field);
        if (unmapped.length) error(unmapped.map(e => e.message).join('; '));
        return;
      }
      const match = BACKEND_FIELD_ERRORS.find(([needle]) => message.includes(needle));
      if (match) setRemoteErrors(r => ({ ...r, [match[1]]: t(match[2]) }));
      else error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={sharedStyles.overlay} onClick={onClose}>
      <div className={`${sharedStyles.modal} ${styles.modalWide}`} onClick={e => e.stopPropagation()}>
        <div className={sharedStyles.header}>
          <h3>{isEdit ? t('offlineSales.editCustomer') : t('offlineSales.addCustomer')}</h3>
          <button onClick={onClose} className={sharedStyles.closeBtn}><X size={18} /></button>
        </div>

        <form noValidate onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0, minWidth: 0 }}>
          <div className={sharedStyles.body}>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label>{t('offlineSales.cusCode')}</label>
                <div className={styles.inputWrapper}>
                  <Hash className={styles.inputIcon} size={16} />
                  <input
                    value={form.cusCode}
                    onChange={set('cusCode')}
                    onBlur={touch('cusCode')}
                    maxLength={20}
                    className={fieldError('cusCode') ? styles.error : ''}
                    placeholder={t('offlineSales.cusCodePlaceholder')}
                  />
                </div>
                {errorText('cusCode')}
              </div>
              <div className={styles.formGroup}>
                <label>{t('offlineSales.customerName')} *</label>
                <div className={styles.inputWrapper}>
                  <User className={styles.inputIcon} size={16} />
                  <input
                    value={form.name}
                    onChange={set('name')}
                    onBlur={touch('name')}
                    className={fieldError('name') ? styles.error : ''}
                    placeholder={t('offlineSales.customerNamePlaceholder', 'Enter customer name')}
                  />
                </div>
                {errorText('name')}
              </div>
              <div className={styles.formGroup}>
                <label>{t('offlineSales.customerPhone')}</label>
                <PhoneNumberInput
                  value={form.phone}
                  onChange={val => setForm(f => ({ ...f, phone: val }))}
                  onValidationChange={(_, msg) => setPhoneFormatErrors(p => ({ ...p, phone: msg || '' }))}
                  error={extraPhoneError('phone')}
                  placeholder={t('offlineSales.customerPhone')}
                />
              </div>
              <div className={styles.formGroup}>
                <label>{t('offlineSales.customerSecondaryPhone')}</label>
                <PhoneNumberInput
                  value={form.secondaryPhone}
                  onChange={val => setForm(f => ({ ...f, secondaryPhone: val }))}
                  onValidationChange={(_, msg) => setPhoneFormatErrors(p => ({ ...p, secondaryPhone: msg || '' }))}
                  error={extraPhoneError('secondaryPhone')}
                  placeholder={t('offlineSales.customerSecondaryPhone')}
                />
              </div>
              <div className={styles.formGroup}>
                <label>{t('offlineSales.customerEmail')}</label>
                <div className={styles.inputWrapper}>
                  <Mail className={styles.inputIcon} size={16} />
                  <input
                    type="email"
                    value={form.email}
                    onChange={set('email')}
                    onBlur={touch('email')}
                    className={fieldError('email') ? styles.error : ''}
                    placeholder={t('offlineSales.customerEmailPlaceholder', 'Enter email address')}
                  />
                </div>
                {errorText('email')}
              </div>
              <div className={styles.formGroup}>
                <label>{t('offlineSales.customerCity')}</label>
                <div className={styles.cityInputWrapper}>
                  <MapPin className={styles.inputIcon} size={16} />
                  <input
                    type="text"
                    value={citySearch}
                    onChange={e => handleCitySearchChange(e.target.value)}
                    onKeyDown={handleCityKeyDownWrapper}
                    onFocus={showCityDropdown}
                    onBlur={() => setTimeout(hideCityResults, 150)}
                    placeholder={t('offlineSales.searchCity')}
                    autoComplete="off"
                  />
                  {citySearch && (
                    <button type="button" onClick={handleClearCity} className={styles.clearCityButton}>
                      <X size={12} />
                    </button>
                  )}
                  {showCityResults && filteredCities.length > 0 && (
                    <div className={styles.citySearchResults}>
                      {filteredCities.map((city, idx) => (
                        <div
                          key={city.id}
                          className={`${styles.cityResultItem} ${idx === citySelectedIndex ? styles.selected : ''}`}
                          onMouseDown={() => handleCitySelect(city)}
                        >
                          <div className={styles.cityName}>{city.name}</div>
                          {city.District && (
                            <div className={styles.cityDetails}>{city.District.name}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                <label>{t('offlineSales.customerAddress')}</label>
                <div className={styles.inputWrapper}>
                  <MapPin className={`${styles.inputIcon} ${styles.textareaIcon}`} size={16} />
                  <textarea
                    value={form.address}
                    onChange={set('address')}
                    rows={2}
                    placeholder={t('offlineSales.customerAddressPlaceholder', 'Enter address')}
                  />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>{t('offlineSales.creditLimit')}</label>
                <div className={styles.inputWrapperNoIcon}>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.creditLimit}
                    onChange={set('creditLimit')}
                    onBlur={touch('creditLimit')}
                    className={fieldError('creditLimit') ? styles.error : ''}
                    placeholder={t('offlineSales.creditLimitPlaceholder')}
                  />
                </div>
                {errorText('creditLimit')}
              </div>
              <div className={styles.formGroup}>
                <label>{t('offlineSales.creditTermDays')}</label>
                <div className={styles.inputWrapperNoIcon}>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={form.creditTermDays}
                    onChange={set('creditTermDays')}
                    onBlur={touch('creditTermDays')}
                    className={fieldError('creditTermDays') ? styles.error : ''}
                    placeholder={t('offlineSales.creditTermDaysPlaceholder')}
                  />
                </div>
                {errorText('creditTermDays')}
              </div>
              <div className={styles.formGroup}>
                <label>{t('offlineSales.obAmount')}</label>
                <div className={styles.inputWrapper}>
                  <DollarSign className={styles.inputIcon} size={16} />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={openingBalance.amount}
                    onChange={e => { setOpeningBalance(ob => ({ ...ob, amount: e.target.value })); clearRemote('obAmount'); }}
                    onBlur={touch('obAmount')}
                    disabled={obLocked}
                    className={fieldError('obAmount') ? styles.error : ''}
                    placeholder="0.00"
                  />
                </div>
                {errorText('obAmount')}
              </div>
              <div className={styles.formGroup}>
                <label>{t('offlineSales.obAsOfDate')}{parseFloat(openingBalance.amount) > 0 && ' *'}</label>
                <DatePicker
                  value={openingBalance.asOfDate}
                  onChange={v => { setOpeningBalance(ob => ({ ...ob, asOfDate: v })); setTouched(tc => ({ ...tc, obDate: true })); clearRemote('obDate'); }}
                  maxDate={todayStr()}
                  disabled={obLocked}
                  placeholder={t('offlineSales.chqSelectDate')}
                />
                {errorText('obDate')}
              </div>
              {obLocked && (
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <span className={styles.errorText}>
                    <Lock size={12} /> {t('offlineSales.obLocked', { paid: formatCurrency(obPaid) })}
                  </span>
                </div>
              )}
              <div className={styles.formGroup}>
                <label>{t('offlineSales.customerType')}</label>
                <Dropdown
                  value={form.customerTypeId}
                  onChange={value => setForm(f => ({ ...f, customerTypeId: value || null }))}
                  options={customerTypes.map(ct => ({ value: ct.id, label: ct.label }))}
                  placeholder={t('offlineSales.selectCustomerType')}
                  icon={<Tag size={16} />}
                />
              </div>
              <div className={styles.formGroup}>
                <label>{t('offlineSales.customerCategory')}</label>
                <div className={styles.categoryRow}>
                  <div className={styles.categoryDropdown}>
                    <Dropdown
                      value={form.customerCategoryId}
                      onChange={value => setForm(f => ({ ...f, customerCategoryId: value || null }))}
                      options={customerCategories.map(cc => ({ value: cc.id, label: cc.name }))}
                      placeholder={t('offlineSales.selectCustomerCategory')}
                      icon={<Layers size={16} />}
                      searchable
                    />
                  </div>
                  {!showCategoryCreate && (
                    <button
                      type="button"
                      className={styles.addCategoryButton}
                      onClick={() => setShowCategoryCreate(true)}
                      title={t('offlineSales.addCustomerCategory')}
                    >
                      <Plus size={16} />
                    </button>
                  )}
                </div>
                {showCategoryCreate && (
                  <div className={styles.createInputGroup}>
                    <input
                      autoFocus
                      type="text"
                      value={newCategoryName}
                      onChange={e => setNewCategoryName(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { e.preventDefault(); handleCreateCategory(); }
                        if (e.key === 'Escape') { e.stopPropagation(); handleCancelCategoryCreate(); }
                      }}
                      maxLength={100}
                      className={styles.createInput}
                      placeholder={t('offlineSales.customerCategoryNamePlaceholder')}
                      disabled={creatingCategory}
                    />
                    <button
                      type="button"
                      className={styles.createButton}
                      onClick={handleCreateCategory}
                      disabled={creatingCategory || !newCategoryName.trim()}
                    >
                      {creatingCategory ? <div className={styles.createSpinner} /> : <Check size={14} />}
                    </button>
                    <button
                      type="button"
                      className={styles.cancelButton}
                      onClick={handleCancelCategoryCreate}
                      disabled={creatingCategory}
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>
              <div className={styles.formGroup}>
                <label>{t('offlineSales.tinNumber')}</label>
                <div className={styles.inputWrapper}>
                  <Hash className={styles.inputIcon} size={16} />
                  <input
                    value={form.tinNumber}
                    onChange={set('tinNumber')}
                    maxLength={50}
                    placeholder={t('offlineSales.tinNumberPlaceholder')}
                  />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>{t('offlineSales.vatNumber')}</label>
                <div className={styles.inputWrapper}>
                  <Hash className={styles.inputIcon} size={16} />
                  <input
                    value={form.vatNumber}
                    onChange={set('vatNumber')}
                    maxLength={50}
                    placeholder={t('offlineSales.vatNumberPlaceholder')}
                  />
                </div>
              </div>
              <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                <label>{t('offlineSales.customerNotes')}</label>
                <div className={styles.inputWrapper}>
                  <FileText className={`${styles.inputIcon} ${styles.textareaIcon}`} size={16} />
                  <textarea
                    value={form.notes}
                    onChange={set('notes')}
                    rows={2}
                    placeholder={t('offlineSales.customerNotesPlaceholder', 'Add any notes about this customer')}
                  />
                </div>
              </div>
              <div className={styles.locationSection}>
                <div className={styles.sectionHeader}>
                  <MapPin className={styles.sectionIcon} size={16} />
                  <h4>{t('offlineSales.customerLocation')}</h4>
                </div>
                <CustomerLocationPicker value={selectedLocation} onChange={handleLocationChange} />
              </div>
              {isEdit && (
                <div className={`${styles.formGroup} ${sharedStyles.toggleRow}`}>
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={form.isActive}
                    onChange={set('isActive')}
                  />
                  <label htmlFor="isActive">{t('offlineSales.activeCustomer')}</label>
                </div>
              )}
            </div>
          </div>

          <div className={sharedStyles.footer}>
            <button type="button" onClick={onClose} className={sharedStyles.cancelBtn}>
              {t('common.cancel', 'Cancel')}
            </button>
            <button type="submit" disabled={saving || (submitAttempted && hasErrors)} className={sharedStyles.confirmBtn}>
              {saving ? '...' : t('common.save', 'Save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerModal;
